import React, {
  FC,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react'
import useDebounce from '../../../hooks/useDebounce';
import useAccessGroupUsers, { Users } from '../../../hooks/useAccessGroupUsers'
import { styled, PixelTheme } from "@pixel/core/styles";
import Checkbox from '@pixel/core/Checkbox';
import CircularProgress from '@pixel/core/CircularProgress';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const EmployeeAutoComplete = styled(Autocomplete)(
  ({ theme }: { theme?: PixelTheme }) => ({
    flex: "1 0 auto",
    display: "inline-flex",
    padding: theme.size.focusWidth
  })
);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface AccessGroupAutoCompleteProps {
  isMakeInactiveGrayout?: boolean
  value: Users
  accessGroupEid: string
  onChange: (event: any, newValue: Users, reason: string) => void
  optional?: boolean
}
export default function AccessGroupAutoComplete (props: AccessGroupAutoCompleteProps) {
  const {
    isMakeInactiveGrayout,
    value,
    accessGroupEid,
    onChange,
    optional = false,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [page, setPage] = useState(0);

  const [options, setOptions] = useState<Array<Users>>([]);
  const [textInput, setTextInput] = useState(value?.name ?? '');
  const [timer, setTimer] = useState(null);
  const debouncedSearchTerm = useDebounce(textInput, 2500);

  const {
    users,
    usersCount,
    loading,
    error,
  } = useAccessGroupUsers({
    page,
    accessGroupEid,
    keywords: textInput,
    onNewResults: (response) => {
      setOptions(response.employeeDropdownListDtos)
      if(response.employeeDropdownListDtos.length < response.pageSize && (((response.pageNumber + 1) * response.pageSize) < response.totalCount)){
            setPage(page => page + 1)
      }
    },
    onNextPageResults: (response) => {
      setOptions(currentOptions => [...currentOptions, ...response.employeeDropdownListDtos])
      if(response.employeeDropdownListDtos.length < response.pageSize && (((response.pageNumber + 1) * response.pageSize) < response.totalCount)){
            setPage(page => page + 1)
      }
    }
  })

  const scrollBoxRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const lastPageLength = useRef(0);

  useEffect(() => {
    setPage(0);
    setTextInput(textInput);
  }, [debouncedSearchTerm]);

  const handleListBoxScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    scrollPositionRef.current = listboxNode.scrollTop;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight
      && !loading
      && options.length !== lastPageLength.current
    ) {
      setPage(page => page + 1)
      lastPageLength.current = options.length;
    }
  };

  useLayoutEffect(() => {
    if (scrollBoxRef.current) { scrollBoxRef.current.scrollTop = scrollPositionRef.current; }
  }, [options.length]);

  return ( 
    <EmployeeAutoComplete
      disabled = {isMakeInactiveGrayout}
      multiple
      limitTags={3}
      value={value}
      disableCloseOnSelect
      open={open}
      onClose={() => {
        setOpen(false);
        setTextInput('');
      }}
      onOpen={() => {
        setOpen(true);
      }}
      ListboxProps={{
        ref: scrollBoxRef,
        onScroll: handleListBoxScroll,
      }}
      isOptionEqualToValue={(option: Users, value: Users) => option.eid === value.eid}
      getOptionLabel={(option: Users) => option.name}
      getValueLabel={(option: Users) => option.name}
      options={options}
      loading={loading}
      currentPage={page}
      onChange={(event: any, newValue: Users, reason: string) => {
        if (typeof onChange === 'function') {
          onChange(event, newValue, reason)
        }
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Add Employees"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            onChange: (event) => {
              clearTimeout(timer);
              const newTimer = setTimeout(() => {
                setTextInput(event.target.value);
              }, 500);
              setTimer(newTimer);
            }
          }}
        />
      )}
      renderOption={(props, option: Users, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.name}
        </li>
      )}
    />
  )
}